import { useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Zap } from "lucide-react";
import type { BatchDetailResponse, RunResponse } from "../../gen/models";
import {
  extractRunModel,
  extractRunTokens,
  extractRunCost,
  extractRunDuration,
  type RunTokens,
} from "../run/runUtils";

export interface BatchCurationTelemetry {
  run: RunResponse | null;
  runId: string | null;
  model?: string;
  tokens: RunTokens;
  cost: number;
  durationMs?: number;
  hasTelemetry: boolean;
  status?: string;
}

export function useBatchCurationRun(batch?: BatchDetailResponse | null): BatchCurationTelemetry {
  if (!batch) {
    return {
      run: null,
      runId: null,
      tokens: { inTok: 0, outTok: 0, total: 0, cached: 0 },
      cost: 0,
      hasTelemetry: false,
    };
  }

  const run = (batch.curation_run as RunResponse) || null;
  const runId = batch.run_id || run?.id || null;
  const model = run ? extractRunModel(run) : undefined;
  const tokens = run ? extractRunTokens(run) : { inTok: 0, outTok: 0, total: 0, cached: 0 };
  const cost = run ? extractRunCost(run) : 0;
  const durationMs = run ? extractRunDuration(run) : undefined;
  const status = run?.status || (batch.status === "processing" ? "running" : undefined);
  const hasTelemetry = Boolean(run && (tokens.total > 0 || cost > 0 || durationMs !== undefined));

  return {
    run,
    runId,
    model,
    tokens,
    cost,
    durationMs,
    hasTelemetry,
    status,
  };
}

export function BatchModelBadge({
  model,
  className = "",
}: {
  model: string;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono text-stone-600 dark:text-stone-300 bg-stone-100 dark:bg-stone-800/80 border border-stone-200/80 dark:border-stone-700/80 shrink-0 ${className}`}
      title={`Model: ${model}`}
    >
      {model}
    </span>
  );
}

export function BatchReviewerRunLink({
  runId,
  className = "",
}: {
  runId: string;
  className?: string;
}) {
  return (
    <Link
      to={`/runs/${runId}`}
      className={`inline-flex items-center gap-1 text-[11px] font-mono text-emerald-600 hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300 hover:underline transition shrink-0 ${className}`}
      title="Inspect Reviewer Agent Run Trace"
    >
      <Zap className="w-3 h-3" />
      <span>Reviewer Run</span>
    </Link>
  );
}

export function BatchCostBreakdownPopover({
  cost,
  tokens,
  durationMs,
  model,
  runId,
}: {
  cost: number;
  tokens: RunTokens;
  durationMs?: number;
  model?: string;
  runId?: string | null;
}) {
  const [showCostBreakdown, setShowCostBreakdown] = useState(false);

  const formatLatency = (ms?: number) => {
    if (!ms || ms <= 0) return "0ms";
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.round(ms)}ms`;
  };

  return (
    <div
      className="relative inline-flex items-center"
      onMouseEnter={() => setShowCostBreakdown(true)}
      onMouseLeave={() => setShowCostBreakdown(false)}
    >
      <button
        type="button"
        onClick={() => setShowCostBreakdown((prev) => !prev)}
        className="inline-flex items-center gap-1 cursor-pointer hover:text-stone-700 dark:hover:text-stone-200 transition-colors focus:outline-hidden"
        title="Click or hover to inspect batch processing cost and run breakdown"
      >
        <Coins className="w-3 h-3 text-amber-500 shrink-0" />
        <span>
          Cost:{" "}
          <strong className="text-stone-700 dark:text-stone-300 font-medium underline decoration-stone-400/50 dark:decoration-stone-600 decoration-dotted underline-offset-3 font-mono">
            ${cost < 0.01 ? cost.toFixed(4) : cost.toFixed(3)}
          </strong>
        </span>
      </button>

      {/* Hover / Click Breakdown Popover */}
      {showCostBreakdown && (
        <div
          className="absolute top-full left-0 mt-2 w-80 md:w-96 rounded-xl border border-stone-200 dark:border-stone-800 bg-white/95 dark:bg-stone-900/95 backdrop-blur-md p-3.5 shadow-xl z-50 text-xs animate-in fade-in duration-100"
          onMouseEnter={() => setShowCostBreakdown(true)}
          onMouseLeave={() => setShowCostBreakdown(false)}
        >
          <div className="flex items-center justify-between pb-2 border-b border-stone-100 dark:border-stone-800">
            <div className="flex items-center gap-1.5 font-medium text-stone-800 dark:text-stone-200">
              <Coins className="w-3.5 h-3.5 text-amber-500" />
              <span>Feedback Processing Cost Breakdown</span>
            </div>
            <span className="text-[10px] text-stone-400 dark:text-stone-500 font-mono">
              1 run
            </span>
          </div>

          <div className="py-2 space-y-2">
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center justify-between font-medium">
                <span className="flex items-center gap-1.5 text-stone-700 dark:text-stone-300">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-indigo-500" />
                  <span className="truncate">Reviewer Agent (Batch Curation)</span>
                </span>
                <span className="font-mono text-stone-900 dark:text-stone-100 shrink-0 ml-2">
                  ${cost < 0.01 ? cost.toFixed(4) : cost.toFixed(3)}
                </span>
              </div>
              <div className="flex items-center justify-between text-[10px] text-stone-400 dark:text-stone-500 pl-3 font-mono">
                <span>
                  {tokens.total.toLocaleString()} tok
                  {tokens.inTok > 0 ? ` (${tokens.inTok.toLocaleString()} in / ${tokens.outTok.toLocaleString()} out)` : ""}
                </span>
                <span>
                  {formatLatency(durationMs)}
                </span>
              </div>
              {model && (
                <div className="flex items-center justify-between text-[10px] text-stone-400 dark:text-stone-500 pl-3 font-mono">
                  <span>Model</span>
                  <span className="text-stone-600 dark:text-stone-300 font-semibold">{model}</span>
                </div>
              )}
              {runId && (
                <div className="flex items-center justify-between text-[10px] text-stone-400 dark:text-stone-500 pl-3 font-mono pt-1">
                  <span>Trace ID</span>
                  <Link
                    to={`/runs/${runId}`}
                    className="text-emerald-600 hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300 hover:underline flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Zap className="w-2.5 h-2.5" />
                    <span>{runId.slice(0, 8)}...</span>
                  </Link>
                </div>
              )}
            </div>
          </div>

          <div className="pt-2 border-t border-stone-100 dark:border-stone-800 space-y-1">
            <div className="flex items-center justify-between font-semibold text-stone-900 dark:text-stone-100">
              <span>Total</span>
              <div className="flex items-center gap-2 font-mono text-[11px]">
                <span>
                  {tokens.total.toLocaleString()} tok
                  {tokens.inTok > 0 ? ` (${tokens.inTok.toLocaleString()} in / ${tokens.outTok.toLocaleString()} out)` : ""}
                </span>
                <span>•</span>
                <span className="text-emerald-600 dark:text-emerald-400 font-bold">
                  ${cost < 0.01 ? cost.toFixed(4) : cost.toFixed(3)}
                </span>
              </div>
            </div>
            {tokens.cached > 0 && (
              <div className="text-[10px] text-indigo-600 dark:text-indigo-400 font-mono text-right">
                ⚡ {tokens.cached.toLocaleString()} prompt tokens served from cache
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function BatchTelemetryRow({
  tokens,
  cost,
  durationMs,
  model,
  runId,
  className = "",
}: {
  tokens: RunTokens;
  cost: number;
  durationMs?: number;
  model?: string;
  runId?: string | null;
  className?: string;
}) {
  const hasTelemetry = tokens.total > 0 || cost > 0 || durationMs !== undefined;
  if (!hasTelemetry && !runId && !model) return null;

  return (
    <div className={`flex items-center gap-2 text-[11px] text-stone-500 dark:text-stone-400 flex-wrap ${className}`}>
      {tokens.total > 0 && (
        <span>
          Tokens:{" "}
          <strong className="text-stone-700 dark:text-stone-200 font-medium">
            {tokens.total.toLocaleString()}
          </strong>
          {tokens.inTok > 0 && (
            <span className="text-[10px] text-stone-400 dark:text-stone-500 font-mono ml-1">
              ({tokens.inTok.toLocaleString()} in / {tokens.outTok.toLocaleString()} out)
            </span>
          )}
          {tokens.cached > 0 && (
            <span
              className="text-[10px] text-indigo-600 dark:text-indigo-400 font-mono ml-1"
              title={`${tokens.cached.toLocaleString()} prompt tokens served from cache`}
            >
              ⚡{tokens.cached.toLocaleString()} cached
            </span>
          )}
        </span>
      )}

      {(cost > 0 || durationMs !== undefined) && (
        <>
          {tokens.total > 0 && <span>•</span>}
          <BatchCostBreakdownPopover
            cost={cost}
            tokens={tokens}
            durationMs={durationMs}
            model={model}
            runId={runId}
          />
        </>
      )}

      {model && (
        <>
          {(tokens.total > 0 || cost > 0 || durationMs !== undefined) && <span>•</span>}
          <span className="flex items-center gap-1">
            <span>Model:</span>
            <BatchModelBadge model={model} />
          </span>
        </>
      )}

      {runId && (
        <>
          {(tokens.total > 0 || cost > 0 || durationMs !== undefined || model) && <span>•</span>}
          <BatchReviewerRunLink runId={runId} />
        </>
      )}
    </div>
  );
}

